import type { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ShieldCheck } from 'lucide-react';
import { z } from 'zod';
import { InviteSuperAdminUserRequest } from '@bliks-io/contracts';
import { useInviteSuperAdminUser } from '@/pages/admin/api/mutations/useSuperAdminUsers';
import { Input, Label, cn } from '@bliks-io/ui';
import { FormDialog } from '@/components/dialogs/FormDialog';
import { applyApiErrorToForm } from '@/lib/errors';
import type { ApiError } from '@/lib/agent.types';

interface InviteSuperAdminDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function InviteSuperAdminDialog({ open, onOpenChange }: InviteSuperAdminDialogProps) {
  const { t } = useTranslation();
  const inviteUser = useInviteSuperAdminUser();

  const defaultValues: InviteSuperAdminUserRequest = {
    firstName: '',
    lastName: '',
    email: '',
    language: 'en'
  };

  const onSubmit = async (
    data: InviteSuperAdminUserRequest,
    form: UseFormReturn<InviteSuperAdminUserRequest>
  ) => {
    try {
      await inviteUser.mutateAsync(data);
      onOpenChange(false);
    } catch (error: unknown) {
      // An already-registered email attaches inline to the email input; anything
      // else (schema failure, network, 5xx) collapses to a root message, which
      // FormDialog renders in its alert.
      applyApiErrorToForm(error as ApiError, form.setError, t, { fields: ['email'] });
    }
  };

  return (
    <FormDialog<InviteSuperAdminUserRequest>
      open={open}
      onOpenChange={onOpenChange}
      title={t('adminSettings.superAdmin.inviteTitle')}
      description={t('adminSettings.superAdmin.inviteDescription')}
      icon={ShieldCheck}
      defaultValues={defaultValues}
      onSubmit={onSubmit}
      submitLabel={t('adminSettings.superAdmin.inviteSend')}
      size="xs"
    >
      {({ form }) => {
        const {
          register,
          formState: { errors }
        } = form;
        return (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="firstName">{t('common.FirstName')}</Label>
              <Input
                id="firstName"
                {...register('firstName', {
                  required: t('adminSettings.superAdmin.firstNameRequired')
                })}
                placeholder={t('organization.members.firstNamePlaceholder')}
                className={cn(errors.firstName && 'border-destructive')}
              />
              {errors.firstName && (
                <p className="text-sm text-destructive">{errors.firstName.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="lastName">{t('common.LastName')}</Label>
              <Input
                id="lastName"
                {...register('lastName', {
                  required: t('adminSettings.superAdmin.lastNameRequired')
                })}
                placeholder={t('organization.members.lastNamePlaceholder')}
                className={cn(errors.lastName && 'border-destructive')}
              />
              {errors.lastName && (
                <p className="text-sm text-destructive">{errors.lastName.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">{t('common.Email')}</Label>
              <Input
                id="email"
                type="email"
                {...register('email', {
                  required: t('adminSettings.superAdmin.emailRequired'),
                  pattern: {
                    value: z.regexes.email,
                    message: t('adminSettings.superAdmin.emailInvalid')
                  }
                })}
                placeholder={t('organization.members.emailPlaceholder')}
                className={cn(errors.email && 'border-destructive')}
              />
              {errors.email && <p className="text-sm text-destructive">{errors.email.message}</p>}
            </div>
          </div>
        );
      }}
    </FormDialog>
  );
}
